import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import React, { ReactNode } from "react";

import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { Typography } from "new-components/typography/Typography";

export type ModalSize = "small" | "medium" | "fullScreen";

export type ModalProps = {
  icon?: React.JSX.Element;
  body: React.JSX.Element | React.JSX.Element[] | null;
  title?: string;
  /**
   * @default center
   */
  titleAlignment?: "center" | "left";
  subtitle?: string;
  open: boolean;
  onClose: VoidFunction;
  onConfirmation?: VoidFunction;

  /**
   * @default medium
   */
  size?: ModalSize;

  /** Override the default max height for the chosen size. */
  maxHeight?: string;

  /** Override the default max width for the chosen size. */
  maxWidth?: string;

  /**
   * @default ok
   */
  confirmationButtonTitle?: string;

  /**
   * @default cancel
   */
  cancelButtonTitle?: string;
  onCancel?: VoidFunction;
  isConfirmationLoading?: boolean;
  isConfirmationDisabled?: boolean;
  isCancelDisabled?: boolean;
  footer?: ReactNode;
  isFluid?: boolean;
};

type ModalVariables = {
  maxWidth: string;
  maxHeight: string;
  fullWidth: boolean;
};

const MODAL_SIZE_TO_MODAL_VARIABLES: Record<ModalSize, ModalVariables> = {
  small: {
    maxWidth: "550px",
    maxHeight: "395px",
    fullWidth: false,
  },
  medium: {
    maxWidth: "900px",
    maxHeight: "650px",
    fullWidth: false,
  },
  fullScreen: {
    maxWidth: "95%",
    maxHeight: "95%",
    fullWidth: true,
  },
};

export function Modal({
  body,
  title,
  open,
  onClose,
  onConfirmation,
  size = "medium",
  maxHeight: maxHeightOverride,
  maxWidth: maxWidthOverride,
  icon: Icon,
  titleAlignment = "center",
  subtitle,
  confirmationButtonTitle = "OK",
  cancelButtonTitle = "Cancel",
  onCancel,
  isConfirmationLoading = false,
  isConfirmationDisabled = false,
  isCancelDisabled = false,
  footer,
  isFluid = false,
}: ModalProps) {
  const { maxWidth: sizeMaxWidth, maxHeight: sizeMaxHeight, fullWidth } = MODAL_SIZE_TO_MODAL_VARIABLES[size];
  const maxWidth = maxWidthOverride ?? sizeMaxWidth;
  const maxHeight = maxHeightOverride ?? sizeMaxHeight;

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onClose();
  };

  const handleCancel = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onCancel?.();
  };

  const handleConfirmation = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onConfirmation?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onClick={(event) => event.stopPropagation()}
      PaperProps={{
        sx: {
          maxWidth: "100%",
          maxHeight,
          margin: "8px",
          borderRadius: 0,
          ...(!isFluid && {
            width: "100%",
            height: "100%",
          }),
          [`@media (min-width:768px)`]: {
            margin: "32px",
            maxWidth,
          },
        },
      }}
      fullWidth={fullWidth}
    >
      <DialogTitle
        id={`modal-title-${title ?? subtitle}`}
        variant="subtitle"
        className="h-[50px] bg-primary-deep flex px-[10px] items-center"
      >
        {Icon}
        <Typography
          variant="title"
          fontWeight="medium"
          className="text-white w-full mx-[10px]"
          textAlign={titleAlignment}
          noWrap
        >
          {title}
        </Typography>
        <Tooltip title="Close">
          <IconButton aria-label="close" onClick={handleClose} sx={{ padding: 0 }}>
            <CloseIcon className="text-white" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "neutral.main", px: { xs: "10px", md: "20px" }, pb: "0px" }}>
        <Stack
          direction="column"
          alignItems="center"
          divider={<Divider className="border-black/[.15]" orientation="horizontal" flexItem />}
          height="100%"
        >
          {subtitle && (
            <Box id={`modal-subtitle-${title ?? subtitle}`} height={70} display="flex" alignItems="center">
              <Typography variant="subtitle" fontWeight="semibold" align="center" className="text-neutral-darkest">
                {subtitle}
              </Typography>
            </Box>
          )}

          {size === "fullScreen" ? (
            <Box id={`modal-body-${title ?? subtitle}`} display="flex" height="100%" width="100%">
              {Array.isArray(body)
                ? body.map((part, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>{part}</React.Fragment>
                  ))
                : body}
            </Box>
          ) : (
            <Box
              id={`modal-body-${title ?? subtitle}`}
              display="flex"
              height="100%"
              width="100%"
              alignItems="center"
              justifyContent="center"
            >
              {Array.isArray(body)
                ? body.map((part, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>{part}</React.Fragment>
                  ))
                : body}
            </Box>
          )}

          {(onCancel || onConfirmation) && (
            <Stack
              id={`modal-buttons-${title ?? subtitle}`}
              height="100px"
              width="100%"
              direction="row"
              spacing="10px"
              alignItems="center"
              justifyContent="center"
            >
              {onCancel && (
                <GroundlightButton
                  sx={{
                    minWidth: "200px",
                  }}
                  onClick={handleCancel}
                  variant="plain"
                  disabled={isCancelDisabled}
                >
                  {cancelButtonTitle}
                </GroundlightButton>
              )}
              {onConfirmation && (
                <GroundlightButton
                  sx={{
                    minWidth: "200px",
                  }}
                  onClick={handleConfirmation}
                  variant="filled"
                  disabled={isConfirmationDisabled || !!isConfirmationLoading}
                >
                  {isConfirmationLoading ? <CircularProgress size={24} /> : confirmationButtonTitle}
                </GroundlightButton>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      {footer}
    </Dialog>
  );
}
